import { useEffect, useState } from "react";
import { Loader2, HardHat, Plus, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const TechRow = ({ tech, onToggle, onReinvite }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async (active) => {
    setBusy(true);
    try {
      await api.patch(`/admin/technicians/${tech.id}`, { active });
      onToggle(tech.id, active);
      toast.success(active ? "Technician activated" : "Technician deactivated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const reinvite = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/technicians/${tech.id}/invite`);
      toast.success(`Invite sent to ${tech.email}`);
      onReinvite();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-testid={`admin-tech-${tech.id}`} className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-brand-ink">{tech.name}</p>
          <p className="text-xs text-zinc-500">{tech.email}{tech.phone ? ` · ${tech.phone}` : ""}</p>
          <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide ${tech.has_password ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
            {tech.has_password ? "Password set" : "Invite pending"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={reinvite} disabled={busy} data-testid={`admin-tech-reinvite-${tech.id}`}
            className="text-xs font-bold text-brand hover:text-brand-dark flex items-center gap-1 disabled:opacity-50">
            <MailPlus className="w-3.5 h-3.5" /> Resend invite
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-zinc-500">{tech.active ? "Active" : "Off"}</span>
            <Switch data-testid={`admin-tech-active-${tech.id}`} checked={tech.active} disabled={busy} onCheckedChange={toggle} />
          </div>
        </div>
      </div>
    </li>
  );
};

export const AdminTechnicians = () => {
  const [techs, setTechs] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/technicians").then((r) => setTechs(r.data.technicians)).catch(() => setTechs([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/technicians", form);
      toast.success(`Technician created — invite emailed to ${form.email}`);
      setForm({ name: "", email: "", phone: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="admin-technicians-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <h2 className="font-heading font-bold text-lg text-brand-ink mb-1 flex items-center gap-2">
        <HardHat className="w-5 h-5 text-brand" /> Technicians (Admin)
      </h2>
      <p className="text-sm text-zinc-600 mb-4">Create technician accounts. They get an email link to set their own password, then can only file visit reports.</p>
      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-brand-subtle border border-zinc-200 rounded-sm p-4">
        <Input data-testid="admin-tech-name-input" required placeholder="Full name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm bg-white" />
        <Input data-testid="admin-tech-email-input" required type="email" placeholder="Email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm bg-white" />
        <Input data-testid="admin-tech-phone-input" placeholder="Phone" value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm bg-white" />
        <button type="submit" disabled={adding} data-testid="admin-tech-create-btn"
          className="bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-4 py-2 rounded-sm text-sm flex items-center justify-center gap-1.5 transition-colors duration-200">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create &amp; Invite
        </button>
      </form>
      <div className="mt-4">
        {techs === null && <Loader2 className="w-6 h-6 text-brand animate-spin" />}
        {techs?.length === 0 && <p data-testid="admin-techs-empty" className="text-sm text-zinc-600">No technicians yet.</p>}
        {techs?.length > 0 && (
          <ul className="divide-y divide-zinc-100">
            {techs.map((t) => (
              <TechRow key={t.id} tech={t} onReinvite={load}
                onToggle={(id, active) => setTechs((prev) => prev.map((x) => (x.id === id ? { ...x, active } : x)))} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
